import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Home,
  Video,
  Building,
  CheckCircle,
  XCircle,
  MapPin,
  Phone,
  Navigation,
} from 'lucide-react';
import { formatDate, getInitials } from '../lib/utils';

const genBookingId = (apt) => {
  const prefix = apt.service_type === 'home_visit' ? 'HV' : apt.service_type === 'online' ? 'ON' : 'CL';
  const num = (apt.id || '').replace(/\D/g, '').slice(0, 5).padStart(5, '0');
  return `PHY-${prefix}-${num}`;
};

export const DoctorAppointmentCard = ({ apt, onAction, profile }) => {
  const isHome = apt.service_type === 'home_visit';
  const isOnline = apt.service_type === 'online';
  const typeLabel = isHome ? 'Home Visit' : isOnline ? 'Online Consultation' : 'Clinic Visit';
  const TypeIcon = isHome ? Home : isOnline ? Video : Building;
  const statusColors = {
    pending: 'bg-amber-100 text-amber-800 border-amber-200',
    confirmed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    completed: 'bg-blue-100 text-blue-800 border-blue-200',
    cancelled: 'bg-red-100 text-red-800 border-red-200',
    rejected: 'bg-slate-100 text-slate-600 border-slate-200',
  };
  const headerColors = {
    pending: 'from-amber-500 to-amber-600',
    confirmed: 'from-emerald-500 to-emerald-600',
    completed: 'from-blue-500 to-blue-600',
    cancelled: 'from-red-500 to-red-600',
    rejected: 'from-slate-400 to-slate-500',
  };

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-testid={`apt-card-${apt.id}`}>
      <div className={`bg-gradient-to-r ${headerColors[apt.status] || headerColors.pending} px-4 py-2.5 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <TypeIcon className="w-4 h-4 text-white" />
          <span className="text-white text-sm font-semibold">{apt.status === 'pending' ? 'New Booking Request' : `${apt.status.charAt(0).toUpperCase() + apt.status.slice(1)} ${typeLabel}`}</span>
        </div>
        <span className="text-white/80 text-xs font-mono">{genBookingId(apt)}</span>
      </div>
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-[#E0F2FE] flex items-center justify-center font-bold text-[#0F62FE]">
            {getInitials(apt.patient?.name || 'P')}
          </div>
          <div>
            <p className="font-semibold text-slate-900">{apt.patient?.name || 'Patient'}</p>
            <p className="text-xs text-slate-500">{apt.patient?.phone || ''}</p>
          </div>
          <Badge className={`ml-auto ${statusColors[apt.status] || ''} capitalize text-xs`}>{apt.status}</Badge>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm bg-slate-50 rounded-lg p-3">
          {apt.symptoms && (
            <div className="col-span-2">
              <span className="text-slate-400 text-xs">Reason for Visit</span>
              <p className="text-slate-800 font-medium">{apt.symptoms}</p>
            </div>
          )}
          <div>
            <span className="text-slate-400 text-xs">Date</span>
            <p className="text-slate-800 font-medium">{formatDate(apt.appointment_date)}</p>
          </div>
          <div>
            <span className="text-slate-400 text-xs">Time</span>
            <p className="text-slate-800 font-medium">{apt.time_slot}</p>
          </div>
          <div>
            <span className="text-slate-400 text-xs">Type</span>
            <p className="text-slate-800 font-medium flex items-center gap-1"><TypeIcon className="w-3.5 h-3.5" />{typeLabel}</p>
          </div>
          <div>
            <span className="text-slate-400 text-xs">Fee</span>
            <p className="text-slate-800 font-medium">₹{apt.fee || 500}</p>
          </div>
          {!isHome && !isOnline && profile?.clinic_name && (
            <div className="col-span-2">
              <span className="text-slate-400 text-xs">Clinic</span>
              <p className="text-slate-800 font-medium flex items-center gap-1"><Building className="w-3.5 h-3.5 text-slate-400" />{profile.clinic_name}</p>
            </div>
          )}
          {isHome && apt.address && (
            <div className="col-span-2">
              <span className="text-slate-400 text-xs">Address</span>
              <p className="text-slate-800 font-medium flex items-start gap-1"><MapPin className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />{apt.address}</p>
            </div>
          )}
          {apt.notes && (
            <div className="col-span-2">
              <span className="text-slate-400 text-xs">Notes</span>
              <p className="text-slate-700">{apt.notes}</p>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          {apt.patient?.phone && (
            <a href={`tel:${apt.patient.phone}`} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors" data-testid={`call-patient-${apt.id}`}>
              <Phone className="w-3.5 h-3.5" />Call Patient
            </a>
          )}
          {isHome && apt.status === 'confirmed' && (
            <button onClick={() => onAction(apt.id, 'on_the_way')} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors" data-testid={`otw-${apt.id}`}>
              <Navigation className="w-3.5 h-3.5" />On The Way
            </button>
          )}
          {apt.status === 'pending' && (
            <>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs" onClick={() => onAction(apt.id, 'confirmed')} data-testid={`accept-${apt.id}`}>
                <CheckCircle className="w-3.5 h-3.5 mr-1" />Accept
              </Button>
              <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 h-8 text-xs" onClick={() => onAction(apt.id, 'rejected')} data-testid={`reject-${apt.id}`}>
                <XCircle className="w-3.5 h-3.5 mr-1" />Reject
              </Button>
            </>
          )}
          {(apt.status === 'confirmed') && (
            <>
              <Button size="sm" className="bg-[#0F62FE] hover:bg-[#0050E6] h-8 text-xs" onClick={() => onAction(apt.id, 'completed')} data-testid={`complete-${apt.id}`}>
                <CheckCircle className="w-3.5 h-3.5 mr-1" />Mark Completed
              </Button>
              <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 h-8 text-xs" onClick={() => onAction(apt.id, 'cancelled')} data-testid={`cancel-${apt.id}`}>
                <XCircle className="w-3.5 h-3.5 mr-1" />Cancel
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DoctorAppointmentCard;
